import React, { createContext, useContext, ReactNode } from 'react';
import { CURRENCY_CONFIG } from '../api';

interface CurrencyContextType {
  currency: typeof CURRENCY_CONFIG;
  formatPrice: (amount: number) => string;
  parsePrice: (priceStr: string) => number;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  // Format price with Indian number formatting
  const formatPrice = (amount: number): string => {
    if (isNaN(amount)) return '₹0';
    
    // Convert to string and add Indian number formatting
    const amountStr = amount.toString();
    let result = '';
    let count = 0;
    
    // Process from right to left
    for (let i = amountStr.length - 1; i >= 0; i--) {
      result = amountStr[i] + result;
      count++;
      
      // Add comma after first 3 digits, then after every 2 digits
      if (count === 3 && i !== 0) {
        result = ',' + result;
      } else if (count > 3 && count % 2 === 0 && i !== 0) {
        result = ',' + result;
      }
    }
    
    return '₹' + result;
  };

  // Parse price string to number (remove currency symbols and commas)
  const parsePrice = (priceStr: string): number => {
    const cleaned = priceStr.replace(/[₹,]/g, '');
    return parseFloat(cleaned) || 0;
  };

  const value: CurrencyContextType = {
    currency: CURRENCY_CONFIG,
    formatPrice,
    parsePrice
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
